import { useEffect, useState } from 'react';

/**
 * ═══ THE FLAGS ═══
 *
 * A row of waving flags across the top of Houses and Elections, one per house, in the colour the
 * school chose, ordered by this season's standing.
 *
 * ⚠ IT IS DECORATION THAT CARRIES INFORMATION, WHICH IS THE ONLY KIND WORTH THE PIXELS. The flags
 * are ordered by points and the leader's flag flies highest and largest, so a child walking past a
 * corridor screen learns the standings from the picture before reading a single number. A row of
 * pretty flags in a random order would be an ornament; this is the scoreboard, drawn.
 *
 * ⚠ A TIE IS DRAWN AS A TIE. If two houses are level at the top, BOTH fly high. The server decides
 * who is leading (see HouseBoard.LeaderIds) precisely so this component cannot get it wrong by
 * taking the first row of a sorted list — an assembly full of children will notice.
 *
 * ⚠ THE COLOUR IS THE SCHOOL'S, NOT A PALETTE OF OURS. Houses here are Red, Blue, Green, Yellow far
 * more often than not, but plenty of schools run Tagore/Nehru/Gandhi/Bose or Phoenix/Griffin, in
 * whatever colours they have had on a blazer badge for forty years. Every colour comes from
 * house.colorHex and nothing here overrides it.
 *
 * ⚠ AND IT RESPECTS prefers-reduced-motion. A permanent animation on a screen somebody has to use
 * all day is a genuine accessibility problem, not a taste one — for vestibular disorders it can
 * cause actual nausea. Reduced motion gets the same flags, still, with no loss of information,
 * because the information is in the order and the height, not the waving.
 */

export interface FlagHouse {
  id: string;
  name: string;
  colorHex: string;
  points: number;
}

/**
 * A cloth-like flag. ⚠ Drawn as an SVG path rather than a rectangle with a CSS skew, because a
 * flag needs a curved trailing edge and a shadow fold to read as cloth at 40 pixels tall. The
 * animation shifts the path's control points, so the fabric ripples rather than the whole shape
 * sliding about.
 */
function Flag({ house, leading, index, still }: {
  house: FlagHouse; leading: boolean; index: number; still: boolean;
}) {
  const h = leading ? 46 : 36;
  const w = leading ? 62 : 50;
  const pole = leading ? 96 : 78;

  return (
    <div className="hf-item" title={`${house.name} — ${house.points} points`}>
      <div className="hf-pole" style={{ height: pole }}>
        {/* the flag itself, hung from the top of the pole */}
        <svg
          className={still ? 'hf-flag' : 'hf-flag hf-wave'}
          width={w}
          height={h}
          viewBox="0 0 62 46"
          style={{ animationDelay: `${(index % 5) * -0.42}s` }}
          aria-hidden="true"
        >
          <defs>
            {/* The fold: the same colour darkened, so one piece of cloth reads as two planes. */}
            <linearGradient id={`hfg-${house.id}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={house.colorHex} stopOpacity="1" />
              <stop offset="55%" stopColor={house.colorHex} stopOpacity="0.82" />
              <stop offset="100%" stopColor={house.colorHex} stopOpacity="1" />
            </linearGradient>
          </defs>
          <path
            className="hf-cloth"
            d="M0 2 C 18 -4, 30 10, 46 4 C 54 1, 58 2, 62 1 L 62 33 C 58 34, 54 33, 46 36 C 30 42, 18 28, 0 34 Z"
            fill={`url(#hfg-${house.id})`}
          />
        </svg>
        {/* ⚠ The pole is drawn AFTER the flag so the cloth appears to hang behind it. */}
        <span className="hf-mast" />
      </div>

      <div className="hf-label">
        <span className="hf-name" style={{ color: house.colorHex }}>{house.name}</span>
        <span className="hf-points">{house.points}</span>
      </div>
    </div>
  );
}

export function HouseFlags({ houses, leaderIds, isTie, season, caption }: {
  houses: FlagHouse[];
  leaderIds: string[];
  isTie: boolean;
  season: string;
  caption?: string;
}) {
  /**
   * ⚠ READ ONCE AND WATCHED. Somebody who turns motion off in their OS while the page is open
   * should get a still flag without reloading — and on a corridor display that never reloads,
   * "without reloading" is the only way it will ever take effect.
   */
  const [still, setStill] = useState(false);
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const mq = matchMedia('(prefers-reduced-motion: reduce)');
    const set = () => setStill(mq.matches);
    set();
    mq.addEventListener('change', set);
    return () => mq.removeEventListener('change', set);
  }, []);

  if (houses.length === 0) return null;

  const leaders = new Set(leaderIds);

  return (
    <div className="house-flags" role="group" aria-label={`House standings, ${season}`}>
      <div className="hf-row">
        {houses.map((h, i) => (
          <Flag key={h.id} house={h} index={i} still={still} leading={leaders.has(h.id)} />
        ))}
      </div>

      {/* ⚠ The standings in words as well as in flags. Colour and height are not accessible on
          their own, and this line is also what a screen reader announces. */}
      <p className="hf-caption">
        {caption ?? (
          leaders.size === 0
            ? `${season} — no points awarded yet`
            : isTie
              ? `${season} — level at the top: ${houses.filter((h) => leaders.has(h.id)).map((h) => h.name).join(' and ')}`
              : `${season} — ${houses.find((h) => leaders.has(h.id))?.name} leading on ${houses.find((h) => leaders.has(h.id))?.points} points`
        )}
      </p>
    </div>
  );
}
